import { DialogContent, Dialog, Box, LinearProgress, CircularProgress, Typography, Stack } from '@mui/material';
import { useRef, useState } from 'react';
import { LoadMore } from 'components/LoadMore';
import { useFeatures } from 'hooks/useFeatures';
import { ROOT_MARGIN } from 'utils/constants';
import { SelectOption } from 'generated/graphql';
import MobileHeader from '../MobileHeader';
import SearchAddResult from './SearchAddResult';
import SearchInputField from './SearchInputField';
import SearchListResults from './SearchListResults';

const MobileSearchResults = ({
  open,
  anchorRef,
  inputValue,
  filteredOptions,
  isFetching = false,
  isDisabled = false,
  hasNextPage = false,
  isFetchingMore = false,
  loadMoreIndex,
  onInputChange,
  onClear,
  onFieldClick,
  onResultClick,
  onAdd,
  onLoadMore = () => {},
  setOpen,
}: {
  open: boolean;
  anchorRef: React.RefObject<HTMLDivElement | null>;
  inputValue: string;
  filteredOptions: SelectOption[];
  isFetching?: boolean;
  isDisabled?: boolean;
  hasNextPage?: boolean;
  isFetchingMore?: boolean;
  loadMoreIndex?: number;
  onInputChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined;
  onClear?: () => void;
  onFieldClick: () => void;
  onResultClick: (option: string, optionEntityID: string) => void;
  onAdd?: () => void;
  onLoadMore?: () => void;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { isEnabled } = useFeatures();
  const isQuotesSearchEnabled = isEnabled('QuotesSearch');
  const [dialogContentElement, setDialogContentElement] = useState<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  if (isDisabled) return null;

  const showInitialLoader = isFetching && filteredOptions.length === 0;
  const addResult = onAdd && <SearchAddResult inputValue={inputValue} onAdd={onAdd} />;

  return (
    <Dialog
      open={open}
      fullScreen
      slotProps={{ transition: { onEntered: () => isQuotesSearchEnabled && inputRef.current?.focus() } }}
    >
      <DialogContent
        ref={setDialogContentElement}
        sx={{
          padding: 0,
          position: 'relative',
          ...(isQuotesSearchEnabled && { display: 'flex', flexDirection: 'column' }),
        }}
      >
        <MobileHeader
          overrideBackButton={() => setOpen(false)}
          isActionsFilling={isQuotesSearchEnabled}
          isLoading={isQuotesSearchEnabled && isFetching}
          loadingLabel="Searching"
          actions={
            <Box ref={anchorRef} sx={isQuotesSearchEnabled ? { flex: 1, minWidth: 0 } : undefined}>
              <SearchInputField
                inputValue={inputValue}
                isFetching={isFetching}
                onInputChange={onInputChange}
                onClick={onFieldClick}
                onClear={onClear}
                inputRef={inputRef}
              />
            </Box>
          }
        />
        {!isQuotesSearchEnabled && (
          <Box sx={{ height: 3 }} aria-live="polite">
            {isFetching && <LinearProgress aria-label="Searching" />}
          </Box>
        )}

        {showInitialLoader ? (
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              py: 4,
            }}
          >
            <CircularProgress size={18} color="inherit" />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              Searching…
            </Typography>
          </Stack>
        ) : (
          <Box
            sx={{
              opacity: isFetching ? 0.55 : 1,
              pointerEvents: isFetching ? 'none' : 'auto',
              transition: 'opacity 120ms ease-out',
            }}
            aria-busy={isFetching}
          >
            <SearchListResults
              filteredOptions={filteredOptions}
              inputValue={inputValue}
              onOptionClick={onResultClick}
              loadMoreTriggerIndex={loadMoreIndex ?? filteredOptions.length - 1}
              loadMoreTrigger={
                hasNextPage && (
                  <LoadMore
                    isFetching={isFetchingMore}
                    hasNextPage={hasNextPage}
                    onEndReached={onLoadMore}
                    rootMargin={ROOT_MARGIN}
                    root={dialogContentElement}
                  />
                )
              }
            />
            {isFetchingMore && <LinearProgress aria-label="Loading more results" />}
            {!isQuotesSearchEnabled && !hasNextPage && addResult}
          </Box>
        )}
        {isQuotesSearchEnabled && addResult && (
          <Box sx={{ position: 'sticky', bottom: 0, mt: 'auto', zIndex: 1, bgcolor: 'background.paper' }}>
            {addResult}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MobileSearchResults;
